import { uniq, uniqBy } from 'lodash';
import { DodFactSet, DodFilter, DodFilters, DodProductSubDimension, DodRunConfig } from '@/types/DodRun';
import {
    DodFilterType,
    DodProductFilterTypes,
    DodProductParentFilterTypes,
    DodTimePeriodFilterTypes,
    RowColConfig,
} from '@/components/DodConfigEditor/types';
import { alert } from '@/components/form/ByzzerModal';
import { DodSelectionPreviewNode } from './DodFilterPreview';

export function filterValuesToStrings(filter?: DodFilter<string[] | 'all'>, defaultValue: string[] = []): string[] {
    if (!filter) return defaultValue;

    const { values, summedSelections } = filter;
    if (values === 'all' || summedSelections.some((ss) => ss.values === 'all')) {
        return defaultValue;
    }

    return uniq([
        ...valuesToStrings(values),
        ...summedSelections.map((sum) => valuesToStrings(sum.values)).flat(), // returns an array of arrays
    ]);
}

export function isSelectedMarketNotAccessible(runconfig: DodRunConfig): boolean {
    const { values, summedSelections } = runconfig.filters.markets;
    const allValues = [...values, ...summedSelections.map((ss) => ss.values)].flat();
    const count = uniqBy(allValues, 'key').length;
    return count < 1;
}

export function alertSelectionLimitExceed(displayTabName: string, limit?: number) {
    return alert({
        content: `As a free user you can only select ${limit} ${displayTabName}. To see more, reach out to your account executive to upgrade your subscription.`,
    });
}

export function valuesToStrings(values: string[] | 'all', defaultValue: string[] = []): string[] {
    if (Array.isArray(values)) {
        return values;
    } else {
        return defaultValue;
    }
}

export function filterHasExplicitValues(filter: DodFilter<string[] | 'all'> | DodFilter): boolean {
    if (hasArrayValues(filter.values)) {
        return true;
    }
    if (filter.summedSelections.some((sum) => hasArrayValues(sum.values))) {
        return true;
    }

    return false;
}

export function filterHasValues(filter: DodFilter<string[] | 'all'> | DodFilter): boolean {
    if (
        (Array.isArray(filter.values) && filter.values.length) ||
        filter.values === 'all' ||
        filter.summedSelections.length
    ) {
        return true;
    }
    return false;
}

export function hasArrayValues(values: string[] | 'all'): boolean {
    return Boolean(Array.isArray(values) && values.length);
}

export function getKeyByValue(object, value) {
    return Object.keys(object).find((key) => object[key] === value);
}

export function isCharacteristicFilter(filterType: DodFilterType): boolean {
    return ['characteristics', 'customCharacteristics'].includes(filterType.type);
}

export function isPpgFilter(filterType: DodFilterType): boolean {
    return ['ppgs'].includes(filterType.type);
}

export function isMarketResetRequired(filterType: DodFilterType, value: DodFilters): boolean {
    // todo: add logic to determine if selected markets will be impacted
    return (
        filterType.type === 'categories' &&
        Boolean(value.markets.values.length || value.markets.summedSelections.length)
    );
}

export function isTimePeriodFilterTypes(filterType: string): boolean {
    return Object.values(DodTimePeriodFilterTypes).includes(filterType as DodTimePeriodFilterTypes);
}

export function isProductFilterTypes(filterType: string): boolean {
    return Object.values(DodProductFilterTypes).includes(filterType as DodProductFilterTypes);
}

export function isParentProductFilterTypes(filterType: string): boolean {
    return Object.values(DodProductParentFilterTypes).includes(filterType as DodProductParentFilterTypes);
}

export function isCharacteristic(type: string): boolean {
    return ['characteristics', 'customCharacteristics'].includes(type);
}

export const defaultProductOrder: DodProductSubDimension[] = [
    'departments',
    'superCategories',
    'categories',
    'subcategories',
    'parentCompanies',
    'manufacturers',
    'brands',
    'upcs',
    'productDescriptions',
];

export const DodFactSuffixes = {
    yearAgo: ` YA`,
    changeYearAgo: ` Change vs Year-Ago`,
    perChangeYearAgo: ` % Change vs Year-Ago`,
    twoYearAgo: ` 2 Year Ago`,
    changeTwoYearAgo: ` Change vs 2 Year-Ago`,
    perChangeTwoYearAgo: ` % Change vs 2 Year-Ago`,
    threeYearAgo: ` 3 Year Ago`,
    changeThreeYearAgo: ` Change vs 3 Year-Ago`,
    perChangeThreeYearAgo: ` % Change vs 3 Year-Ago`,
    fourYearAgo: ` 4 Year Ago`,
    changeFourYearAgo: ` Change vs 4 Year-Ago`,
    perChangeFourYearAgo: ` % Change vs 4 Year-Ago`,
    shareByCategory: ` % Share By Category`,
    shareByCategoryYearAgo: ` % Share By Category Year-Ago`,
    shareByBrand: ` % Share By Brand`,
    shareByBrandYearAgo: ` % Share By Brand Year-Ago`,
    shareByProductSelections: ` % Share By Product Selections`,
    shareByProductSelectionsYearAgo: ` % Share By Product Selections Year-Ago`,
    shareBySubcategory: ` % Share By Subcategory`,
    shareBySubcategoryYearsAgo: ` % Share By Subcategory Year-Ago`
};

export function factSetToDisplayNames(factSet: DodFactSet): string[] {
    return [...factSetToCoreDisplayNames(factSet), ...factSetToSharedDisplayNames(factSet)];
}

export function factSetToCoreDisplayNames(factSet: DodFactSet): string[] {
    const { fact: currentPeriod, yearAgo, changeVsYearAgo, percentChangeVsYearAgo, twoYearAgo, threeYearAgo, fourYearAgo, changeVsTwoYearAgo, changeVsThreeYearAgo, changeVsFourYearAgo, percentChangeVsFourYearAgo, percentChangeVsThreeYearAgo, percentChangeVsTwoYearAgo } = factSet;

    const values: string[] = [];
    if (currentPeriod) {
        values.push(factSet.display);
    }
    if (yearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.yearAgo}`);
    }
    if (changeVsYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.changeYearAgo}`);
    }
    if (percentChangeVsYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.perChangeYearAgo}`);
    }
    if (twoYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.twoYearAgo}`)
    }
    if (changeVsTwoYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.changeTwoYearAgo}`);
    }
    if (percentChangeVsTwoYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.perChangeTwoYearAgo}`);
    }
    if (threeYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.threeYearAgo}`)
    }
    if (changeVsThreeYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.changeThreeYearAgo}`);
    }
    if (percentChangeVsThreeYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.perChangeThreeYearAgo}`);
    }
    if (fourYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.fourYearAgo}`)
    }
    if (changeVsFourYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.changeFourYearAgo}`);
    }
    if (percentChangeVsFourYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.perChangeFourYearAgo}`);
    }

    return values;
}

export function factSetToSharedDisplayNames(factSet: DodFactSet): string[] {
    const { category, categoryYearAgo, brand, brandYearAgo, productSelections, productSelectionsYearAgo, subCategory, subCategoryYearAgo } = factSet;
    const values: string[] = [];

    if (category) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByCategory}`);
    }
    if (categoryYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByCategoryYearAgo}`);
    }
    if (brand) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByBrand}`);
    }
    if (brandYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByBrandYearAgo}`);
    }
    if (productSelections) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByProductSelections}`);
    }
    if (productSelectionsYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByProductSelectionsYearAgo}`);
    }
    if (subCategory) {
        values.push(`${factSet.display}${DodFactSuffixes.shareBySubcategory}`)
    }
    if (subCategoryYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareBySubcategoryYearsAgo}`)
    }

    return values;
}

export const DodRunMethod = {
    create: 'new run',
    edit: 'rerun',
    modifiedRun: 'modified run',
    layout: 'apply new layout run',
    ScheduledRun: 'scheduled run',
};

export const compareObjects = (obj1, obj2, parentKey = '') => {
    try {
        let diffs: string[] = [];

        const keys1 = obj1 ? Object.keys(obj1) : [];
        const keys2 = obj2 ? Object.keys(obj2) : [];

        // Check keys that are only in obj1
        keys1.forEach((key) => {
            const fullKey = parentKey ? `${parentKey}.${key}` : key;

            if (!keys2.includes(key)) {
                // Only consider keys missing in obj2 if their value in obj1 is not an empty array or undefined
                if (Array.isArray(obj1[key]) && obj1[key].length === 0) {
                    return;
                }
                if (obj1[key] !== undefined) {
                    diffs.push(`Key '${fullKey}' is missing in the second configuration.`);
                }
            } else if (typeof obj1[key] === 'object' && obj1[key] !== null) {
                // Recursively compare objects
                diffs = diffs.concat(compareObjects(obj1[key], obj2[key], fullKey));
            } else if (obj1[key] !== obj2[key]) {
                // Consider undefined or empty arrays as equivalent to missing keys
                if (
                    (Array.isArray(obj1[key]) && obj1[key].length === 0 && obj2[key] === undefined) ||
                    (obj1[key] === undefined && Array.isArray(obj2[key]) && obj2[key].length === 0)
                ) {
                    return; // Treat as equal
                }
                diffs.push(`Value of '${fullKey}' differs: '${obj1[key]}' vs '${obj2[key]}'.`);
            }
        });

        // Check keys that are only in obj2
        keys2.forEach((key) => {
            const fullKey = parentKey ? `${parentKey}.${key}` : key;

            if (!keys1.includes(key)) {
                // Only consider keys missing in obj1 if their value in obj2 is not an empty array or undefined
                if (Array.isArray(obj2[key]) && obj2[key].length === 0) {
                    return;
                }
                if (obj2[key] !== undefined) {
                    diffs.push(`Key '${fullKey}' is missing in the first configuration.`);
                }
            }
        });

        return diffs;
    } catch (err) {
        console.error('Error comparing objects', err);
        throw err;
    }
};

export const marketFolderLabelMapping = {
    'Major Market': 'majorMarkets',
    Division: 'divisions',
    Stateline: 'stateLines',
    Region: 'regions',
    Channel: 'channels',
    Account: 'accounts',
};

export const invertedMarketFolderLabelMapping = Object.fromEntries(
    Object.entries(marketFolderLabelMapping).map(([key, value]) => [value, key])
);

export const weeksSellingFactIds: number[] = [1031];
export const cdiBdiFactIds: number[] = [1021, 1067, 1068, 1069, 1071, 1072];
export const isWorkInProgressFact = (selectedFacts: DodFactSet[]): { disabled: boolean; hoverText: string } => {
    return {
        disabled: selectedFacts.some((fact) => [...weeksSellingFactIds, ...cdiBdiFactIds].includes(fact.id)),
        hoverText:
            'This selection type is not compatible with one of your fact selections. If you wish to include this type of selection in your run, you will need to remove any CDI/BDI and Number of Weeks Selling facts that were included.',
    };
};

// disabling conditions for Weeks selling fact - BYZ-13989
// disabling conditions for CDI/BDI fact - BYZ-13988
export const isWorkInProgressFactsDisabled = ({ fact, runConfig, rowColConfigs }: {
    fact: DodFactSet,
    runConfig: DodRunConfig,
    rowColConfigs: RowColConfig[]
}): boolean => {
    return (
        (isWeeksSellingFact(fact) || isCdiBdiFact(fact)) &&
        (isSubTotalSelected(runConfig) ||
            isCategoriesTotalSelected(runConfig) ||
            isDisabledDueToCategoryHidden(rowColConfigs) ||
            isDisabledDueToStackedProducts(rowColConfigs) ||
            // isDisabledDueToRestrictedProducts(runConfig.filters) ||
            isSummedProductSelected(runConfig.filters) ||
            isSummedMarketSelected(runConfig.filters) ||
            isSummedTimePeriodsSelected(runConfig.filters))
    );
};

export const isWeeksSellingFact = (fact: DodFactSet) => {
    return weeksSellingFactIds.includes(fact.id);
};

export const isCdiBdiFact = (fact: DodFactSet) => {
    return cdiBdiFactIds.includes(fact.id);
};

const isSubTotalSelected = (runConfig: DodRunConfig) => {
    return runConfig.layout.includeSubTotals;
};

const isCategoriesTotalSelected = (runConfig: DodRunConfig) => {
    return runConfig.layout.includeCategoryTotals;
}

const isSummedMarketSelected = (filters: DodFilters) => {
    return Boolean(filters.markets.summedSelections.length);
};

const isSummedTimePeriodsSelected = (filters: DodFilters) => {
    return Boolean(filters.timePeriods.summedSelections.length);
};

const isDisabledDueToRestrictedProducts = (filters: DodFilters) => {
    const productFilters = Object.keys(filters).filter(
        (filter) => filter === 'characteristics' || filter === 'customCharacteristics' || filter === 'ppgs'
    );
    for (const filter of productFilters) {
        if (Object.keys(filters[filter]).length) {
            return true;
        }
    }
    return false;
}

const isSummedProductSelected = (filters: DodFilters) => {
    let isSummedProductSelected: boolean = false;
    const productFilters = Object.keys(filters).filter((filter) => filter !== 'markets' && filter !== 'timePeriods');
    for (const filterType of productFilters) {
        if (
            [
                DodProductFilterTypes.CHARACTERISTICS,
                DodProductFilterTypes.CUSTOM_CHARACTERISTICS,
                DodProductFilterTypes.PPGS,
            ].includes(filterType as DodProductFilterTypes)
        ) {
            const filterKeys = Object.keys(filters[filterType]);
            for (const key of filterKeys) {
                if (filters[filterType][key].summedSelections.length) {
                    isSummedProductSelected = true;
                }
            }
        } else {
            if (filters[filterType].summedSelections.length) {
                isSummedProductSelected = true;
            }
        }
    }
    return isSummedProductSelected;
};

// disable both weeks selling and cdi/bdi facts if hidden products is present in layout
const isDisabledDueToCategoryHidden = (rowColConfigs: RowColConfig[]) => {
    return rowColConfigs.some((rowColConfig) => rowColConfig.type === 'products' && rowColConfig.hide);
};

// disable both weeks selling and cdi/bdi facts if hidden products is present in layout
const isDisabledDueToStackedProducts = (rowColConfigs: RowColConfig[]) => {
    return rowColConfigs.some((rowColConfig) => rowColConfig.type === 'products' && rowColConfig.stack);
};

export function extractTypeValue(type: string) {
    const match = type.match(/^(customCharacteristics|characteristics|ppgs)_(.+)$/);
    return match ? match[2] : null;
}

export const getFilterKey = (option) => {
    let returnValue = '';
    if (isCharacteristicFilter(option.value) || isPpgFilter(option.value)) {
        returnValue = `${option.value.type}_${option.value.data.id || option.value.data}`;
    } else {
        returnValue = option.value.type || option.value;
    }
    return returnValue;
};

export const productsInitialGroups: DodSelectionPreviewNode[] = [
    {
        label: 'Hierarchy',
        filterType: DodProductParentFilterTypes.hierarchy,
        children: [
            { label: 'Department', filterType: DodProductFilterTypes.DEPARTMENTS },
            { label: 'Super Category', filterType: DodProductFilterTypes.SUPER_CATEGORIES },
            { label: 'Category', filterType: DodProductFilterTypes.CATEGORIES },
            { label: 'Subcategory', filterType: DodProductFilterTypes.SUBCATEGORIES },
            { label: 'UPC', filterType: DodProductFilterTypes.UPC },
            { label: 'Product Description', filterType: DodProductFilterTypes.PRODUCT_DESCRIPTIONS },
        ],
    },
    {
        label: 'Brand & Manufacturer',
        filterType: DodProductParentFilterTypes.brandAndManufacturer,
        children: [
            { label: 'Parent Company', filterType: DodProductFilterTypes.PARENT_COMPANIES },
            { label: 'Manufacturer', filterType: DodProductFilterTypes.MANUFACTURERS },
            { label: 'Brand', filterType: DodProductFilterTypes.BRANDS },
        ],
    },
    {
        label: 'Characteristics',
        filterType: DodProductParentFilterTypes.characteristics,
        loadChildren(filters) {
            return Object.entries(filters.characteristics).map(([characteristicDisplayValue, filter]) => ({
                label: filter.displayName,
                filterType: {
                    type: DodProductFilterTypes.CHARACTERISTICS,
                    data: { id: characteristicDisplayValue, displayName: filter.displayName },
                },
                values: filter.values,
                summedSelections: filter.summedSelections,
            }));
        },
    },
    {
        label: 'My Custom Characteristics',
        filterType: DodProductParentFilterTypes.customCharacteristics,
        loadChildren(filters) {
            return Object.entries(filters.customCharacteristics).map(([characteristicDisplayValue, filter]) => ({
                label: filter.displayName,
                filterType: {
                    type: DodProductFilterTypes.CUSTOM_CHARACTERISTICS,
                    data: { id: characteristicDisplayValue, displayName: filter.displayName },
                },
                values: filter.values,
                summedSelections: filter.summedSelections,
            }));
        },
    },
    {
        label: 'My PPGs',
        filterType: DodProductParentFilterTypes.ppgs,
        loadChildren(filters) {
            return Object.entries(filters.ppgs).map(([ppgDisplayValue, filter]) => ({
                label: filter.displayName,
                filterType: {
                    type: DodProductFilterTypes.PPGS,
                    data: { id: ppgDisplayValue, displayName: filter.displayName },
                },
                values: filter.values,
                summedSelections: filter.summedSelections,
            }));
        },
    },
];

export function getFilterType(filterType: DodFilterType | string | undefined): DodFilterType | undefined {
    if (typeof filterType === 'string') {
        return {
            type: filterType
        }
    }
    return filterType;
}